import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send, Trash2, CheckCircle } from 'lucide-react';

const ContactUs = () => {
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [deletionForm, setDeletionForm] = useState({
        email: '',
        description: '',
        reason: 'privacy'
    });

    const [contactStatus, setContactStatus] = useState(null); // 'success', 'error', null
    const [deletionStatus, setDeletionStatus] = useState(null);

    const handleContactSubmit = (e) => {
        e.preventDefault();
        // Mock submission
        console.log('Contact message submitted:', contactForm);
        setContactStatus('success');
        setContactForm({ name: '', email: '', message: '' });
        setTimeout(() => setContactStatus(null), 5000);
    };

    const handleDeletionSubmit = (e) => {
        e.preventDefault();
        // Mock deletion request
        console.log('Deletion request submitted:', deletionForm);
        setDeletionStatus('success');
        setDeletionForm({ email: '', description: '', reason: 'privacy' });
        setTimeout(() => setDeletionStatus(null), 5000);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <header className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center text-gray-600 hover:text-primary transition-colors">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Home
                    </Link>
                    <div className="flex items-center space-x-2">
                        <Mail className="w-6 h-6 text-primary" />
                        <span className="font-bold text-gray-900">Contact & Support</span>
                    </div>
                </div>
            </header>

            <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Contact Form Section */}
                    <div className="space-y-8">
                        <div className="bg-white shadow-lg rounded-2xl p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                                <Send className="w-6 h-6 text-indigo-600 mr-2" />
                                Get in Touch
                            </h2>
                            <p className="text-gray-600 mb-6">
                                Have questions about Intuit LMS? We're here to help! Fill out the form below and our team will get back to you shortly.
                            </p>

                            {contactStatus === 'success' && (
                                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Message sent successfully!
                                </div>
                            )}

                            <form onSubmit={handleContactSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 transition-colors"
                                        value={contactForm.name}
                                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 transition-colors"
                                        value={contactForm.email}
                                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                    <textarea
                                        required
                                        rows="4"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 transition-colors"
                                        value={contactForm.message}
                                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                    />
                                </div>
                                <button type="submit" className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-md">
                                    Send Message
                                </button>
                            </form>
                        </div>

                        {/* Contact Info */}
                        <div className="bg-white shadow-md rounded-2xl p-8">
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <MapPin className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Our Location</h3>
                                        <p className="text-gray-600">Nairobi, Kenya</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Mail className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Email Us</h3>
                                        <p className="text-gray-600">support@intuitlms.online</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <Phone className="w-6 h-6 text-indigo-600 mt-1 mr-3" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Call Us</h3>
                                        <p className="text-gray-600">+254 700 000 000</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Deletion Form Section */}
                    <div className="bg-white shadow-lg rounded-2xl p-8 border-t-4 border-red-500">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <Trash2 className="w-6 h-6 text-red-500 mr-2" />
                            Data Deletion Request
                        </h2>
                        <p className="text-gray-600 mb-6">
                            If you wish to remove your personal data or user-generated content from our platform, please verify your identity and submit a request below.
                            Requests are typically processed within 30 days in accordance with data privacy regulations.
                        </p>

                        {deletionStatus === 'success' && (
                            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                Deletion request submitted successfully. Check your email for confirmation.
                            </div>
                        )}

                        <form onSubmit={handleDeletionSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account Email</label>
                                <input
                                    type="email"
                                    required
                                    placeholder="email@example.com"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 hover:border-red-300 transition-colors"
                                    value={deletionForm.email}
                                    onChange={(e) => setDeletionForm({ ...deletionForm, email: e.target.value })}
                                />
                                <p className="text-xs text-gray-500 mt-1">We will send a confirmation link to this email.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Deletion</label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 hover:border-red-300 transition-colors"
                                    value={deletionForm.reason}
                                    onChange={(e) => setDeletionForm({ ...deletionForm, reason: e.target.value })}
                                >
                                    <option value="privacy">Privacy Concerns</option>
                                    <option value="no_longer_use">I no longer use the service</option>
                                    <option value="duplicate">Duplicate Account</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                                <textarea
                                    rows="3"
                                    placeholder="Describe specific content to delete (if applicable) or any other details..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 hover:border-red-300 transition-colors"
                                    value={deletionForm.description}
                                    onChange={(e) => setDeletionForm({ ...deletionForm, description: e.target.value })}
                                />
                            </div>

                            <button type="submit" className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-md">
                                Submit Deletion Request
                            </button>
                        </form>
                    </div>
                </div>
            </main>

            <footer className="bg-gray-900 text-white py-8 text-center">
                <p className="text-gray-400">© 2025 INTUIT LMS. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default ContactUs;
